/**
 * Site Studio permission keys + minimal provisioning stub (TP-G26 · D23)
 * Canonical site.model.* ids are provisioned for internal actors; legacy aliases retained.
 */

import type { SiteEntityType } from '@shared/tenant-host';

export const SITE_STUDIO_PUBLISH_PERMISSION = 'site:studio:publish' as const;
export const SITE_STUDIO_EDIT_PERMISSION = 'site:studio:edit' as const;

export const SITE_MODEL_EDIT_CAPABILITY = 'site.model.edit' as const;
export const SITE_MODEL_PREVIEW_CAPABILITY = 'site.model.preview' as const;
export const SITE_MODEL_PUBLISH_CAPABILITY = 'site.model.publish' as const;
export const SITE_MODEL_ROLLBACK_CAPABILITY = 'site.model.rollback' as const;
export const SITE_SLUG_MANAGE_CAPABILITY = 'site.slug.manage' as const;
export const SITE_DATA_BINDING_CONFIGURE_CAPABILITY = 'site.data_binding.configure' as const;

export type SiteStudioPermission =
  | typeof SITE_STUDIO_PUBLISH_PERMISSION
  | typeof SITE_STUDIO_EDIT_PERMISSION
  | typeof SITE_MODEL_EDIT_CAPABILITY
  | typeof SITE_MODEL_PREVIEW_CAPABILITY
  | typeof SITE_MODEL_PUBLISH_CAPABILITY
  | typeof SITE_MODEL_ROLLBACK_CAPABILITY
  | typeof SITE_SLUG_MANAGE_CAPABILITY
  | typeof SITE_DATA_BINDING_CONFIGURE_CAPABILITY;

export interface SiteStudioCapabilities {
  canPublish: boolean;
  canEditStudio: boolean;
}

const WRITE_PERMISSION_RE = /^([a-z_]+):(create|update|delete)$/;

const INTERNAL_CANONICAL_SITE_CAPS: readonly string[] = [
  SITE_MODEL_EDIT_CAPABILITY,
  SITE_MODEL_PREVIEW_CAPABILITY,
  SITE_MODEL_PUBLISH_CAPABILITY,
  SITE_MODEL_ROLLBACK_CAPABILITY,
  SITE_SLUG_MANAGE_CAPABILITY,
  SITE_DATA_BINDING_CONFIGURE_CAPABILITY,
  SITE_STUDIO_PUBLISH_PERMISSION,
  SITE_STUDIO_EDIT_PERMISSION,
];

function hasSubdomainWritePermission(permissions: readonly string[]): boolean {
  return permissions.some((perm) => WRITE_PERMISSION_RE.test(perm));
}

/**
 * Minimal provisioning stub — internal entityType gets canonical site caps;
 * external/user only when explicitly present in JWT permissions.
 * Owner wildcard remains forbidden; this is an explicit enumerated grant set.
 */
export function provisionSiteStudioPermissions(
  entityType: SiteEntityType,
  permissions: readonly string[]
): string[] {
  const merged = new Set(permissions);
  if (entityType === 'internal') {
    for (const cap of INTERNAL_CANONICAL_SITE_CAPS) {
      merged.add(cap);
    }
  }
  return [...merged];
}

export function resolveSiteStudioCapabilities(input: {
  role?: string;
  entityType?: SiteEntityType;
  permissions?: readonly string[];
}): SiteStudioCapabilities {
  const permissions = provisionSiteStudioPermissions(
    input.entityType ?? null,
    input.permissions ?? []
  );

  const isOwnerOrPublisher =
    input.role === 'owner' ||
    input.role === 'site_publisher' ||
    permissions.includes(SITE_STUDIO_PUBLISH_PERMISSION) ||
    permissions.includes(SITE_MODEL_PUBLISH_CAPABILITY);

  const canEditStudio =
    input.role === 'owner' ||
    input.role === 'site_publisher' ||
    permissions.includes(SITE_STUDIO_EDIT_PERMISSION) ||
    permissions.includes(SITE_MODEL_EDIT_CAPABILITY) ||
    hasSubdomainWritePermission(permissions);

  return {
    canPublish: isOwnerOrPublisher,
    canEditStudio,
  };
}

export function hasSiteStudioPublishPermission(
  entityType: SiteEntityType,
  permissions: readonly string[]
): boolean {
  return resolveSiteStudioCapabilities({ entityType, permissions }).canPublish;
}
